import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle
} from "react";
import {
  Bell,
  ChevronDown,
  ChevronUp,
  Plus,
  X,
  Clock,
  Calendar,
  AlertTriangle
} from "lucide-react";

const RemainderModal = forwardRef((props, ref) => {
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminders, setReminders] = useState({
    oneTime: {
      enabled: false,
      delay: 3,
      delayUnit: "days"
    },
    recurring: {
      enabled: false,
      interval: 1,
      intervalUnit: "hours",
      startAfter: 1,
      startAfterUnit: "days"
    },
    expiration: {
      enabled: false,
      warningBefore: 24,
      warningBeforeUnit: "hours",
      halfwayWarning: false
    },
    escalation: {
      enabled: false,
      escalateInLast: 48,
      escalateInLastUnit: "hours"
    },
    custom: {
      enabled: false,
      schedule: [{ day: 1, time: "09:00" }]
    }
  });
  useEffect(() => {
    if (!props.data) return;

    const savedReminders = props.data.reminders;
    setReminderEnabled(!!props.data.enabled);
    setReminders((prev) => ({
      oneTime: { ...prev.oneTime, ...savedReminders?.oneTime },
      recurring: { ...prev.recurring, ...savedReminders?.recurring },
      expiration: { ...prev.expiration, ...savedReminders?.expiration },
      escalation: { ...prev.escalation, ...savedReminders?.escalation },
      custom: { ...prev.custom, ...savedReminders?.custom }
    }));
  }, [props.data]);

  const [expandedSections, setExpandedSections] = useState({
    oneTime: false,
    recurring: false,
    expiration: false,
    escalation: false,
    custom: false
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleReminder = (type) => {
    setReminders((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        enabled: !prev[type].enabled
      }
    }));
    if (!reminders[type].enabled) {
      setExpandedSections((prev) => ({ ...prev, [type]: true }));
    }
  };

  const updateReminder = (type, field, value) => {
    setReminders((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const addCustomSchedule = () => {
    setReminders((prev) => ({
      ...prev,
      custom: {
        ...prev.custom,
        schedule: [...prev.custom.schedule, { day: 1, time: "09:00" }]
      }
    }));
  };

  const removeCustomSchedule = (index) => {
    setReminders((prev) => ({
      ...prev,
      custom: {
        ...prev.custom,
        schedule: prev.custom.schedule.filter((_, i) => i !== index)
      }
    }));
  };

  const updateCustomSchedule = (index, field, value) => {
    setReminders((prev) => ({
      ...prev,
      custom: {
        ...prev.custom,
        schedule: prev.custom.schedule.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
  };

  const handleSave = () => {
    const data = {
      enabled: reminderEnabled,
      reminders: reminderEnabled ? reminders : null
    };
    return data;
  };

  useImperativeHandle(ref, () => ({
    handleSave
  }));

  return (
    <div className="text-xs bg-white rounded-lg shadow-sm pt-2 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bell className="w-6 h-6 text-blue-600" />
          <h3 className="text-lg font-[400] text-gray-800">
            Reminder Settings
          </h3>
        </div>

        {/* Toggle Switch */}
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={reminderEnabled}
            onChange={(e) => setReminderEnabled(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>

      {reminderEnabled && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 mb-4">
            Configure automated reminders to keep your signers on track.
            Multiple reminder types can be enabled simultaneously.
          </p>

          {/* One-Time Reminder */}
          <div className="border border-gray-200 rounded-lg">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={reminders.oneTime.enabled}
                    onChange={() => toggleReminder("oneTime")}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label className="font-semibold text-gray-800 cursor-pointer">
                    One-Time Reminder
                  </label>
                </div>
                {reminders.oneTime.enabled && (
                  <button
                    type="button"
                    onClick={() => toggleSection("oneTime")}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    {expandedSections.oneTime ? (
                      <ChevronUp className="w-5 h-5 text-gray-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-600" />
                    )}
                  </button>
                )}
              </div>

              {reminders.oneTime.enabled && expandedSections.oneTime && (
                <div className="mt-4 ml-7 space-y-3">
                  <p className="text-sm text-gray-600">
                    Send a single reminder after a specific time period
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-gray-700">
                      Send reminder after
                    </span>
                    <input
                      type="number"
                      value={reminders.oneTime.delay}
                      onChange={(e) =>
                        updateReminder(
                          "oneTime",
                          "delay",
                          parseInt(e.target.value)
                        )
                      }
                      className="w-20 px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      min="1"
                    />
                    <select
                      value={reminders.oneTime.delayUnit}
                      onChange={(e) =>
                        updateReminder("oneTime", "delayUnit", e.target.value)
                      }
                      className="px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                    </select>
                    <span className="text-sm text-gray-700">
                      from document sent date
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recurring Reminder */}
          <div className="border border-gray-200 rounded-lg">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={reminders.recurring.enabled}
                    onChange={() => toggleReminder("recurring")}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label className="font-semibold text-gray-800 cursor-pointer">
                    Recurring Reminders
                  </label>
                </div>
                {reminders.recurring.enabled && (
                  <button
                    type="button"
                    onClick={() => toggleSection("recurring")}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    {expandedSections.recurring ? (
                      <ChevronUp className="w-5 h-5 text-gray-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-600" />
                    )}
                  </button>
                )}
              </div>

              {reminders.recurring.enabled && expandedSections.recurring && (
                <div className="mt-4 ml-7 space-y-3">
                  <p className="text-sm text-gray-600">
                    Send reminders at regular intervals until signed or expired
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-gray-700">
                      Send reminder every
                    </span>
                    <input
                      type="number"
                      value={reminders.recurring.interval}
                      onChange={(e) =>
                        updateReminder(
                          "recurring",
                          "interval",
                          parseInt(e.target.value)
                        )
                      }
                      className="w-20 px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      min="1"
                    />
                    <select
                      value={reminders.recurring.intervalUnit}
                      onChange={(e) =>
                        updateReminder(
                          "recurring",
                          "intervalUnit",
                          e.target.value
                        )
                      }
                      className="px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="hours">hour(s)</option>
                      <option value="days">day(s)</option>
                      <option value="weeks">week(s)</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-gray-700">
                      Start reminders after
                    </span>
                    <input
                      type="number"
                      value={reminders.recurring.startAfter}
                      onChange={(e) =>
                        updateReminder(
                          "recurring",
                          "startAfter",
                          parseInt(e.target.value)
                        )
                      }
                      className="w-20 px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      min="0"
                    />
                    <select
                      value={reminders.recurring.startAfterUnit}
                      onChange={(e) =>
                        updateReminder(
                          "recurring",
                          "startAfterUnit",
                          e.target.value
                        )
                      }
                      className="px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                    </select>
                    <span className="text-sm text-gray-700">
                      from document sent date
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Expiration Warning */}
          <div className="border border-gray-200 rounded-lg">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={reminders.expiration.enabled}
                    onChange={() => toggleReminder("expiration")}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label className="font-semibold text-gray-800 cursor-pointer">
                    Expiration Warning
                  </label>
                </div>
                {reminders.expiration.enabled && (
                  <button
                    type="button"
                    onClick={() => toggleSection("expiration")}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    {expandedSections.expiration ? (
                      <ChevronUp className="w-5 h-5 text-gray-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-600" />
                    )}
                  </button>
                )}
              </div>

              {reminders.expiration.enabled && expandedSections.expiration && (
                <div className="mt-4 ml-7 space-y-3">
                  <p className="text-sm text-gray-600">
                    Send warning reminders as the deadline approaches
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-gray-700">
                      Send final warning
                    </span>
                    <input
                      type="number"
                      value={reminders.expiration.warningBefore}
                      onChange={(e) =>
                        updateReminder(
                          "expiration",
                          "warningBefore",
                          parseInt(e.target.value)
                        )
                      }
                      className="w-20 px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      min="1"
                    />
                    <select
                      value={reminders.expiration.warningBeforeUnit}
                      onChange={(e) =>
                        updateReminder(
                          "expiration",
                          "warningBeforeUnit",
                          e.target.value
                        )
                      }
                      className="px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                    </select>
                    <span className="text-sm text-gray-700">
                      before expiration
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={reminders.expiration.halfwayWarning}
                      onChange={(e) =>
                        updateReminder(
                          "expiration",
                          "halfwayWarning",
                          e.target.checked
                        )
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label className="text-sm text-gray-700">
                      Send additional warning at halfway point
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Escalation Reminder */}
          <div className="border border-gray-200 rounded-lg">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={reminders.escalation.enabled}
                    onChange={() => toggleReminder("escalation")}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label className="font-semibold text-gray-800 cursor-pointer">
                    Escalation Reminder
                  </label>
                </div>
                {reminders.escalation.enabled && (
                  <button
                    type="button"
                    onClick={() => toggleSection("escalation")}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    {expandedSections.escalation ? (
                      <ChevronUp className="w-5 h-5 text-gray-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-600" />
                    )}
                  </button>
                )}
              </div>

              {reminders.escalation.enabled && expandedSections.escalation && (
                <div className="mt-4 ml-7 space-y-3">
                  <p className="text-sm text-gray-600">
                    Increase reminder frequency as deadline approaches
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-gray-700">
                      Switch to hourly reminders in the last
                    </span>
                    <input
                      type="number"
                      value={reminders.escalation.escalateInLast}
                      onChange={(e) =>
                        updateReminder(
                          "escalation",
                          "escalateInLast",
                          parseInt(e.target.value)
                        )
                      }
                      className="w-20 px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      min="1"
                    />
                    <select
                      value={reminders.escalation.escalateInLastUnit}
                      onChange={(e) =>
                        updateReminder(
                          "escalation",
                          "escalateInLastUnit",
                          e.target.value
                        )
                      }
                      className="px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Custom Schedule */}
          <div className="border border-gray-200 rounded-lg">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={reminders.custom.enabled}
                    onChange={() => toggleReminder("custom")}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label className="font-semibold text-gray-800 cursor-pointer">
                    Custom Schedule
                  </label>
                </div>
                {reminders.custom.enabled && (
                  <button
                    type="button"
                    onClick={() => toggleSection("custom")}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    {expandedSections.custom ? (
                      <ChevronUp className="w-5 h-5 text-gray-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-600" />
                    )}
                  </button>
                )}
              </div>

              {reminders.custom.enabled && expandedSections.custom && (
                <div className="mt-4 ml-7 space-y-3">
                  <p className="text-sm text-gray-600">
                    Define your own reminder schedule with specific days/times
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    {reminders.custom.schedule.map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">Day</span>
                        <input
                          type="number"
                          value={item.day}
                          onChange={(e) =>
                            updateCustomSchedule(
                              index,
                              "day",
                              parseInt(e.target.value)
                            )
                          }
                          className="w-16 px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          min="1"
                        />
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">at</span>
                        <input
                          type="time"
                          value={item.time}
                          onChange={(e) =>
                            updateCustomSchedule(index, "time", e.target.value)
                          }
                          className="px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        {reminders.custom.schedule.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeCustomSchedule(index)}
                            className="p-1 hover:bg-red-100 rounded text-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addCustomSchedule}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border-2 border-dashed border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Another Time
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
RemainderModal.displayName = "RemainderModal";
export default RemainderModal;
